import * as React from 'react';
import { Link } from 'react-router-dom';
import { LogOut } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/states';
import { signOut } from '@/features/auth/api';
import { queryClient } from '@/lib/query';

/**
 * Sign out, then say so.
 *
 * Ending the session is a POST, so a link cannot do it on its own — which is
 * exactly why the old "Sign out" link to the Django admin's logout went
 * nowhere. This page posts on arrival, clears every cached query so nothing
 * from the old session can be shown to the next person, and confirms.
 */
export default function LogoutPage() {
  const [state, setState] = React.useState<'working' | 'done' | 'error'>('working');
  const [message, setMessage] = React.useState('');
  const started = React.useRef(false);

  const run = React.useCallback(() => {
    setState('working');
    signOut()
      .then(() => {
        queryClient.clear();
        setState('done');
      })
      .catch((failure: unknown) => {
        setMessage(failure instanceof Error ? failure.message : 'Could not sign out.');
        setState('error');
      });
  }, []);

  React.useEffect(() => {
    // Strict mode runs effects twice in development; one sign-out is plenty.
    if (started.current) return;
    started.current = true;
    document.title = 'Signed out · Admin Console';
    run();
  }, [run]);

  return (
    <div className="flex min-h-full items-center justify-center bg-surface-muted px-4 py-10 text-ink">
      <div className="w-full max-w-[22rem] rounded-xl border border-line bg-surface p-6 text-center shadow-pop">
        <span className="mx-auto flex size-10 items-center justify-center rounded-full bg-surface-muted text-ink-3">
          {state === 'working' ? <Spinner /> : <LogOut className="size-5" />}
        </span>
        {state === 'working' ? (
          <p role="status" className="mt-3 text-[13px] text-ink-2">
            Signing you out…
          </p>
        ) : state === 'done' ? (
          <>
            <h1 className="mt-3 text-[16px] font-semibold">You are signed out</h1>
            <p className="mt-1 text-[12px] text-ink-3">Your session on this device has ended.</p>
            <Button variant="primary" size="md" asChild className="mt-4">
              <Link to="/login">Sign in again</Link>
            </Button>
          </>
        ) : (
          <>
            <h1 className="mt-3 text-[16px] font-semibold">Could not sign out</h1>
            <p role="alert" className="mt-1 text-[12px] text-danger">
              {message}
            </p>
            <Button variant="secondary" size="md" onClick={run} className="mt-4">
              Try again
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
